import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Shield, Building2, Package, LogOut, Users, MapPin, Check, X,
  Plus, Pencil, Trash2, Save, Settings, Tag, Star, Percent, Clock, Sparkles,
  Smartphone, Key, Copy, Eye, EyeOff, ShieldCheck, ShieldOff, Loader2, Lock,
  Bot, Puzzle, AlertTriangle, Bell, Phone, ChevronRight, Menu as MenuIcon, Search,
  Bug, Image, Volume2, ExternalLink, CheckCircle2, Archive, ChevronDown, Upload, Mail,
  TrendingUp, BarChart3, Database, FlaskConical, FileText, Calendar, CreditCard
} from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "../../lib/fileUrl";
import Sicurezza from "../Sicurezza";
import { useConfirm } from "../../hooks/useConfirm";
import SuperAdminSocietaDetail from "./SuperAdminSocietaDetail";
import CompanyAccountsPanel from "./CompanyAccountsPanel";
import VenditoriPanel from "./VenditoriPanel";
import PrezziPanel from "./PrezziPanel";
import SuperAdminSidebar from "./SuperAdminSidebar";
import ReportProvvigioniPanel from "./ReportProvvigioniPanel";
import ContrattiTemplatePanel from "./ContrattiTemplatePanel";
import AnalyticsPanel, { AnalyticsMiniCard } from "./AnalyticsPanel";
import BackupPanel from "./BackupPanel";
import BackupHealthBanner from "./BackupHealthBanner";
import DemoPanel from "./DemoPanel";
import MaterialiVenditaPanel from "./MaterialiVenditaPanel";
import RegistrazioniLogPanel from "./RegistrazioniLogPanel";
import EmailDiagnosticPanel from "./EmailDiagnosticPanel";
import RecoveryTurniPanel from "./RecoveryTurniPanel";
import TelegramCentralPanel from "./TelegramCentralPanel";
import StripeDiagnosticPanel from "./StripeDiagnosticPanel";
import SuperAdminNotificheBell from "../../components/superadmin/SuperAdminNotificheBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_LABELS = { base: "BASE", addon: "ADD-ON", servizio: "SERVIZIO" };
const TIPO_COLORS = { base: "bg-indigo-100 text-indigo-700", addon: "bg-amber-100 text-amber-700", servizio: "bg-teal-100 text-teal-700" };

// ─── Bundle Pricing Panel ───────────────────────────────
const BundlePricingPanel = () => {
  const [bundle, setBundle] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchBundle = useCallback(async () => {
    try {
      const token = localStorage.getItem("sa_token");
      const res = await axios.get(`${API}/superadmin/bundle-pricing`, { headers: { Authorization: `Bearer ${token}` } });
      setBundle(res.data);
    } catch { setBundle({ nome: "Pacchetto Completo", descrizione: "HACCP + Acquisti + Personale", prezzo_mensile_annuale: 199, attivo: true }); }
  }, []);

  useEffect(() => { fetchBundle(); }, [fetchBundle]);

  const saveBundle = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("sa_token");
      await axios.put(`${API}/superadmin/bundle-pricing`, bundle, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bundle pricing aggiornato!");
    } catch { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  if (!bundle) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Prezzo Bundle (Pacchetto Completo)</h2>
        <Button onClick={saveBundle} disabled={saving} className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold transition-colors" data-testid="save-bundle-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salva"}
        </Button>
      </div>
      <p className="text-sm text-gray-500">Quando un utente seleziona tutti e 3 i pacchetti principali, viene applicato automaticamente il prezzo bundle invece della somma dei singoli.</p>
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-5">
        <div className="flex items-center gap-3">
          <Switch checked={bundle.attivo} onCheckedChange={v => setBundle(b => ({ ...b, attivo: v }))} data-testid="bundle-active-switch" />
          <span className="font-semibold text-sm text-gray-700">{bundle.attivo ? "Bundle attivo" : "Bundle disattivato"}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold text-gray-600">Nome</Label>
            <Input value={bundle.nome} onChange={e => setBundle(b => ({ ...b, nome: e.target.value }))} className="mt-1 rounded-xl" data-testid="bundle-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-600">Descrizione</Label>
            <Input value={bundle.descrizione} onChange={e => setBundle(b => ({ ...b, descrizione: e.target.value }))} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-600">Prezzo mensile (netto + IVA)</Label>
            <Input type="number" value={bundle.prezzo_mensile_annuale} onChange={e => setBundle(b => ({ ...b, prezzo_mensile_annuale: parseFloat(e.target.value) || 0 }))} className="mt-1 rounded-xl" data-testid="bundle-price-annual" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Addon Pricing Panel ────────────────────────────────
const AddonPricingPanel = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem("sa_token");
      const res = await axios.get(`${API}/superadmin/addon-config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
    } catch {
      setConfig({
        telegram_bot: { prezzo_primo_numero: 10, prezzo_numero_extra: 5 },
        locale_aggiuntivo: { locali_inclusi: 1, prezzo_extra_annuale: 20 },
      });
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("sa_token");
      await axios.put(`${API}/superadmin/addon-config`, config, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Configurazione add-on salvata!");
    } catch { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  if (!config) return null;

  const tg = config.telegram_bot || {};
  const loc = config.locale_aggiuntivo || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Configurazione Prezzi Add-on</h2>
        <Button onClick={saveConfig} disabled={saving} className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold transition-colors" data-testid="save-addon-config-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salva"}
        </Button>
      </div>
      <p className="text-sm text-gray-500">Configura i prezzi per gli add-on. Tutti i prezzi sono netti + IVA.</p>

      {/* Telegram Bot */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Bot Telegram - Prezzi Numeri</h3>
            <p className="text-xs text-gray-500">Costo del primo numero e dei numeri aggiuntivi</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold text-gray-600">Costo 1° numero (€/mese netto)</Label>
            <Input type="number" step="0.5" min={0} value={tg.prezzo_primo_numero ?? 10}
              onChange={e => setConfig(c => ({ ...c, telegram_bot: { ...c.telegram_bot, prezzo_primo_numero: parseFloat(e.target.value) || 0 } }))}
              className="mt-1 rounded-xl" data-testid="tg-prezzo-primo" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-600">Costo numeri successivi (€/mese cad.)</Label>
            <Input type="number" step="0.5" min={0} value={tg.prezzo_numero_extra ?? 5}
              onChange={e => setConfig(c => ({ ...c, telegram_bot: { ...c.telegram_bot, prezzo_numero_extra: parseFloat(e.target.value) || 0 } }))}
              className="mt-1 rounded-xl" data-testid="tg-prezzo-extra" />
          </div>
        </div>
      </div>

      {/* Locali Aggiuntivi */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
            <MapPin className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Locali Aggiuntivi</h3>
            <p className="text-xs text-gray-500">Prezzo per ogni locale aggiuntivo</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold text-gray-600">Locali inclusi (gratis)</Label>
            <Input type="number" min={0} value={loc.locali_inclusi ?? 1}
              onChange={e => setConfig(c => ({ ...c, locale_aggiuntivo: { ...c.locale_aggiuntivo, locali_inclusi: parseInt(e.target.value) || 0 } }))}
              className="mt-1 rounded-xl" data-testid="loc-inclusi" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-600">Prezzo extra/mese (netto + IVA)</Label>
            <Input type="number" step="0.5" min={0} value={loc.prezzo_extra_annuale ?? 20}
              onChange={e => setConfig(c => ({ ...c, locale_aggiuntivo: { ...c.locale_aggiuntivo, prezzo_extra_annuale: parseFloat(e.target.value) || 0 } }))}
              className="mt-1 rounded-xl" data-testid="loc-price-annual" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Security 2FA Panel ──────────────────────────────────
const SecurityPanel = () => {
  const [totpStatus, setTotpStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("sa_token")}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/auth/totp/status`, { headers });
      setTotpStatus(res.data);
    } catch (e) {
      console.error("Error fetching TOTP status:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSetup = async () => {
    try {
      const res = await axios.post(`${API}/superadmin/auth/totp/setup`, {}, { headers });
      setSetupData(res.data);
      setConfirmCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore setup TOTP"); }
  };

  const handleConfirm = async () => {
    if (confirmCode.length < 6) return;
    try {
      setConfirming(true);
      const res = await axios.post(`${API}/superadmin/auth/totp/confirm`, { code: confirmCode }, { headers });
      setRecoveryCodes(res.data.recovery_codes);
      setSetupData(null);
      toast.success("2FA attivato con successo!");
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Codice non valido"); }
    finally { setConfirming(false); }
  };

  const handleDisable = async () => {
    if (!await confirm("Sei sicuro di voler disattivare la 2FA?", { confirmLabel: "Disattiva", confirmClass: "bg-amber-600 hover:bg-amber-700" })) return;
    try {
      await axios.post(`${API}/superadmin/auth/totp/disable`, {}, { headers });
      toast.success("2FA disattivato");
      setRecoveryCodes(null);
      setSetupData(null);
      fetchStatus();
    } catch { toast.error("Errore disattivazione"); }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Codici copiati!");
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6" data-testid="security-panel">
      <div className={`p-5 rounded-2xl border ${totpStatus?.totp_enabled ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {totpStatus?.totp_enabled ? <ShieldCheck className="w-6 h-6 text-green-600" /> : <ShieldOff className="w-6 h-6 text-amber-600" />}
            <div>
              <h3 className="font-semibold text-gray-900">{totpStatus?.totp_enabled ? "2FA Attivo" : "2FA Non Attivo"}</h3>
              <p className="text-sm text-gray-500">
                {totpStatus?.totp_enabled
                  ? `Protetto con Google Authenticator. ${totpStatus.recovery_codes_remaining} codici di recupero rimasti.`
                  : "Il tuo account è protetto solo da password. Attiva la 2FA per maggiore sicurezza."}
              </p>
            </div>
          </div>
          {totpStatus?.totp_enabled ? (
            <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700" onClick={handleDisable} data-testid="disable-totp-btn">
              <ShieldOff className="w-4 h-4 mr-1" /> Disattiva
            </Button>
          ) : (
            <Button size="sm" onClick={handleSetup} className="bg-slate-900 hover:bg-slate-700 text-white hover:text-white transition-colors" data-testid="setup-totp-btn">
              <Smartphone className="w-4 h-4 mr-1" /> Attiva 2FA
            </Button>
          )}
        </div>
      </div>

      {setupData && (
        <div className="p-6 rounded-2xl bg-white border border-gray-200 space-y-5" data-testid="totp-setup-flow">
          <h3 className="font-semibold text-gray-900 text-base">Setup Google Authenticator</h3>
          <div className="space-y-3">
            <p className="text-sm text-gray-600"><strong>1.</strong> Apri <strong>Google Authenticator</strong> sul telefono</p>
            <p className="text-sm text-gray-600"><strong>2.</strong> Tocca il <strong>+</strong> e scansiona questo QR code:</p>
          </div>
          <div className="flex justify-center py-2">
            <img src={setupData.qr_code} alt="QR Code TOTP" className="rounded-xl border border-gray-200" style={{ width: 200, height: 200 }} data-testid="totp-qr-code" />
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 mb-1">Oppure inserisci manualmente il codice:</p>
            <code className="text-xs font-mono bg-gray-100 px-3 py-1.5 rounded-lg text-gray-700 select-all" data-testid="totp-secret">{setupData.secret}</code>
          </div>
          <div className="space-y-2 pt-2">
            <p className="text-sm text-gray-600"><strong>3.</strong> Inserisci il codice a 6 cifre generato dall'app:</p>
            <div className="flex gap-2">
              <Input type="text" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-xl tracking-[0.4em] font-mono h-12 rounded-xl flex-1" placeholder="------" maxLength={6} data-testid="totp-confirm-input" />
              <Button onClick={handleConfirm} disabled={confirming || confirmCode.length !== 6}
                className="h-12 px-6 rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold transition-colors" data-testid="totp-confirm-btn">
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div className="p-6 rounded-2xl bg-amber-50 border border-amber-200 space-y-4" data-testid="recovery-codes">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Key className="w-5 h-5 text-amber-600" /><h3 className="font-semibold text-gray-900">Codici di Recupero</h3></div>
            <Button variant="outline" size="sm" onClick={copyRecoveryCodes} className="gap-1.5" data-testid="copy-recovery-btn"><Copy className="w-3.5 h-3.5" /> Copia</Button>
          </div>
          <p className="text-sm text-amber-800">Salva questi codici in un posto sicuro. Ogni codice può essere usato una sola volta per accedere senza l'app authenticator.</p>
          <div className="grid grid-cols-2 gap-2">
            {recoveryCodes.map((code, i) => (
              <code key={i} className="text-sm font-mono bg-white px-3 py-2 rounded-lg text-gray-800 text-center border border-amber-200">{code}</code>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setRecoveryCodes(null)} className="w-full mt-2">Ho salvato i codici, chiudi</Button>
        </div>
      )}

      {!setupData && !recoveryCodes && (
        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Come funziona la 2FA?</h4>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>Ogni volta che accedi, dopo email e password ti viene chiesto un codice</li>
            <li>Il codice viene generato dall'app Google Authenticator sul tuo telefono</li>
            <li>Cambia ogni 30 secondi, quindi nessuno può indovinarlo</li>
            <li>Se perdi il telefono, puoi usare i codici di recupero</li>
          </ul>
        </div>
      )}

      {/* Toggle OTP Email Login */}
      {!totpStatus?.totp_enabled && (
        <div className={`p-5 rounded-2xl border ${totpStatus?.otp_login_enabled !== false ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className={`w-5 h-5 ${totpStatus?.otp_login_enabled !== false ? "text-blue-600" : "text-slate-400"}`} />
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Verifica OTP via Email</h3>
                <p className="text-xs text-gray-500">
                  {totpStatus?.otp_login_enabled !== false
                    ? "Ad ogni login viene inviato un codice di verifica alla tua email."
                    : "OTP email disabilitato. L'accesso avviene solo con email e password."}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await axios.put(`${API}/superadmin/auth/otp-login/toggle`, {}, { headers });
                  toast.success(res.data.message);
                  fetchStatus();
                } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
              }}
              className={totpStatus?.otp_login_enabled !== false
                ? "text-amber-600 border-amber-300 hover:bg-amber-50"
                : "text-blue-600 border-blue-300 hover:bg-blue-50"}
              data-testid="toggle-otp-login-btn"
            >
              {totpStatus?.otp_login_enabled !== false ? "Disabilita" : "Abilita"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// NavItem is now in SuperAdminSidebar.js

// ─── Hub Nav Card ──────────────────────────────────────────
const HubNavCard = ({ icon: Icon, title, description, onClick, iconBg, iconColor, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all text-left w-full group">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: iconBg || "#EEF2FF" }}>
      <Icon className="h-5 w-5" style={{ color: iconColor || "#4f46e5" }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-gray-900 text-sm">{title}</p>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-indigo-400 transition-colors flex-shrink-0" />
  </button>
);

// ─── Breadcrumb back link ──────────────────────────────────
const BackLink = ({ label, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium mb-1">
    <ChevronRight className="h-4 w-4 rotate-180" />{label}
  </button>
);

// ═══════════════════════════════════════════════════════════
// ═══ MAIN DASHBOARD ═══════════════════════════════════════
// ═══════════════════════════════════════════════════════════
const SuperAdminDashboard = ({ token, user, onLogout }) => {
  const confirm = useConfirm();
  const [societies, setSocieties] = useState([]);
  const [packages, setPackages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [trialSettings, setTrialSettings] = useState({ trial_default_giorni: 14, trial_attivo: true });
  const [loading, setLoading] = useState(true);

  // Navigation
  const [activePage, setActivePage] = useState("societies");
  const [selectedSocietaId, setSelectedSocietaId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Dialogs (packages + discount codes only)
  const [pkgDialog, setPkgDialog] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [codeDialog, setCodeDialog] = useState(false);
  const [editCode, setEditCode] = useState(null);

  // Segnalazioni
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [segnalazioneDetail, setSegnalazioneDetail] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [notaRisoluzione, setNotaRisoluzione] = useState("");
  const [archiviaAlResolve, setArchiviaAlResolve] = useState(true);
  const [messaggiTab, setMessaggiTab] = useState("segnalazioni");
  const [archivedSegnalazioni, setArchivedSegnalazioni] = useState([]);
  const [archivioLoaded, setArchivioLoaded] = useState(false);
  const [expandedLocali, setExpandedLocali] = useState({});
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [resolvingPendingId, setResolvingPendingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [socRes, pkgRes, featRes, codesRes, trialRes] = await Promise.all([
        axios.get(`${API}/superadmin/societies`, { headers }),
        axios.get(`${API}/superadmin/packages`, { headers }),
        axios.get(`${API}/superadmin/features`, { headers }),
        axios.get(`${API}/superadmin/discount-codes`, { headers }),
        axios.get(`${API}/superadmin/trial-settings`, { headers }),
      ]);
      setSocieties(socRes.data);
      setPackages(pkgRes.data);
      setFeatures(featRes.data);
      setDiscountCodes(codesRes.data);
      setTrialSettings(trialRes.data);
      // Fetch segnalazioni
      try {
        const segnRes = await axios.get(`${API}/segnalazioni`, { headers });
        setSegnalazioni(segnRes.data);
      } catch (e) { console.warn("[SuperAdminDashboard] resource not yet created:", e); }
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Errore caricamento dati");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Fetch pending requests ───────────────
  const fetchPendingRequests = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${API}/superadmin/pending-requests`, { headers });
      setPendingRequests(res.data);
    } catch (e) { console.warn("[SuperAdminDashboard] silent failure:", e); }
    setLoadingPending(false);
  }, [token]);

  useEffect(() => {
    if (messaggiTab === "richieste") fetchPendingRequests();
  }, [messaggiTab, fetchPendingRequests]);

  const resolvePendingRequest = async (reqId, action) => {
    setResolvingPendingId(reqId);
    try {
      await axios.put(`${API}/superadmin/pending-requests/${reqId}/resolve`, { action }, { headers });
      toast.success(action === "approve" ? "Richiesta approvata e bot attivato!" : "Richiesta rifiutata");
      setPendingRequests(prev => prev.filter(r => r.id !== reqId));
      fetchData(); // refresh pending counts
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
    setResolvingPendingId(null);
  };

  // ─── Package CRUD ─────────────────────────
  const emptyPkg = { nome: "", descrizione: "", tipo: "base", features: [], prezzo_mensile_annuale: 0, sconto_percentuale: 0, in_evidenza: false, ordine: 0, immagine_url: "", wizard_key: "" };
  const [pkgForm, setPkgForm] = useState(emptyPkg);

  const openPkgDialog = async (pkg = null) => {
    setEditPkg(pkg);
    setPkgForm(pkg ? { ...emptyPkg, ...pkg } : { ...emptyPkg });
    try {
      const url = pkg ? `${API}/superadmin/features?editing_package_id=${pkg.id}` : `${API}/superadmin/features`;
      const res = await axios.get(url, { headers });
      setFeatures(res.data);
    } catch (e) { console.error("[SuperAdminDashboard] open pkg dialog failed:", e); }
    setPkgDialog(true);
  };
  const savePkg = async () => {
    try {
      if (editPkg) await axios.put(`${API}/superadmin/packages/${editPkg.id}`, pkgForm, { headers });
      else await axios.post(`${API}/superadmin/packages`, pkgForm, { headers });
      toast.success(editPkg ? "Aggiornato" : "Creato");
      setPkgDialog(false); fetchData();
    } catch { toast.error("Errore salvataggio"); }
  };
  const deletePkg = async (id) => {
    if (!await confirm("Eliminare questo pacchetto?")) return;
    try { await axios.delete(`${API}/superadmin/packages/${id}`, { headers }); toast.success("Eliminato"); fetchData(); } catch { toast.error("Errore"); }
  };
  const toggleFeature = (key) => {
    // Ora le features possono essere condivise tra più pacchetti - nessun blocco
    setPkgForm(p => ({ ...p, features: p.features.includes(key) ? p.features.filter(f => f !== key) : [...p.features, key] }));
  };
  const featureGroups = features.reduce((acc, f) => { if (!acc[f.group]) acc[f.group] = []; acc[f.group].push(f); return acc; }, {});

  // ─── Discount Code CRUD ───────────────────
  const emptyCode = { codice: "", tipo_sconto: "percentuale", valore: 0, applica_a: "tutti", durata_sconto: "primo_mese", scadenza: "", max_utilizzi: 0, attivo: true, note: "" };
  const [codeForm, setCodeForm] = useState(emptyCode);

  const openCodeDialog = (code = null) => {
    setEditCode(code);
    setCodeForm(code ? { ...emptyCode, ...code, scadenza: code.scadenza?.split("T")[0] || "" } : { ...emptyCode });
    setCodeDialog(true);
  };
  const saveCode = async () => {
    try {
      const payload = { ...codeForm };
      if (payload.scadenza) payload.scadenza = payload.scadenza + "T23:59:59";
      if (editCode) await axios.put(`${API}/superadmin/discount-codes/${editCode.id}`, payload, { headers });
      else await axios.post(`${API}/superadmin/discount-codes`, payload, { headers });
      toast.success(editCode ? "Aggiornato" : "Creato");
      setCodeDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const deleteCode = async (id) => {
    if (!await confirm("Eliminare questo codice?")) return;
    try { await axios.delete(`${API}/superadmin/discount-codes/${id}`, { headers }); toast.success("Eliminato"); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  // ─── Toggle society ───────────────────────
  const toggleSoc = async (soc) => {
    try { const res = await axios.put(`${API}/superadmin/societies/${soc.id}/toggle`, {}, { headers }); toast.success(res.data.attivo ? "Attivata" : "Disattivata"); fetchData(); } catch { toast.error("Errore"); }
  };

  // ─── Trial ────────────────────────────────
  const saveTrial = async (field, value) => {
    try {
      await axios.put(`${API}/superadmin/trial-settings`, { ...trialSettings, [field]: value }, { headers });
      setTrialSettings(prev => ({ ...prev, [field]: value }));
      toast.success("Trial aggiornato");
    } catch { toast.error("Errore"); }
  };

  // ─── Helpers ──────────────────────────────
  const pendingTotal = societies.reduce((acc, s) => acc + (s.pending_count || 0), 0);
  const segnalazioniNuove = segnalazioni.filter(s => s.stato === "nuovo").length;
  const messaggiBadge = pendingTotal + segnalazioniNuove;
  const filteredSocieties = societies.filter(soc => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (soc.nome || soc.ragione_sociale || soc.denominazione || "").toLowerCase().includes(q);
  });
  const activeSocs = filteredSocieties.filter(s => s.attivo !== false);
  const inactiveSocs = filteredSocieties.filter(s => s.attivo === false);

  // ─── Card cost calculation ────────────────
  const calcSocCost = (soc) => {
    const sub = soc.subscription || {};
    const dur = sub.durata || "annuale";
    // Usa il costo ufficiale calcolato dal backend (include sconto combo + addon)
    let total = typeof soc.costo_mensile === "number" ? soc.costo_mensile : 0;
    const items = [];
    (sub.package_ids || []).forEach(pid => {
      const pkg = packages.find(p => p.id === pid);
      if (pkg) {
        items.push(pkg.nome);
        // Fallback: se il backend non ha ancora popolato costo_mensile, somma flat
        if (total === 0) total += pkg.prezzo_mensile_annuale || 0;
      }
    });
    const addons = sub.addons || {};
    if (addons.telegram_bot?.numeri_acquistati > 0) {
      items.push(`Bot TG (${addons.telegram_bot.numeri_acquistati})`);
    }
    if ((soc.locali_count || 0) > 1) {
      items.push(`${soc.locali_count} locali`);
    }
    return { total, items, dur };
  };

  const calcRemainingMonths = (soc) => {
    const sub = soc.subscription || {};
    if (sub.gratuito) return null;
    if (sub.trial_giorni > 0 && sub.trial_inizio) {
      const start = new Date(sub.trial_inizio);
      const end = new Date(start.getTime() + sub.trial_giorni * 86400000);
      const now = new Date();
      const daysLeft = Math.max(0, Math.ceil((end - now) / 86400000));
      return { type: "trial", days: daysLeft };
    }
    if (sub.data_inizio) {
      const start = new Date(sub.data_inizio);
      const totalMonths = 12;
      const end = new Date(start);
      end.setMonth(end.getMonth() + totalMonths);
      const now = new Date();
      const monthsLeft = Math.max(0, Math.round((end - now) / (30.44 * 86400000)));
      return { type: "sub", months: monthsLeft };
    }
    return null;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
    </div>
  );

  const handleNavigate = (page) => {
    setSelectedSocietaId(null);
    setActivePage(page);
  };

  // If a society is selected, show its detail page WITH sidebar
  if (selectedSocietaId) {
    return (
      <div className="min-h-screen bg-gray-50 flex" data-testid="superadmin-dashboard">
        <SuperAdminSidebar
          activePage={activePage}
          onNavigate={handleNavigate}
          onLogout={onLogout}
          user={user}
          messaggiBadge={messaggiBadge}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className="flex-1 overflow-y-auto min-w-0">
          {/* Mobile top bar */}
          <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 md:hidden">
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="mobile-menu-btn">
              <MenuIcon className="h-5 w-5 text-gray-600" />
            </button>
            <span className="text-sm font-bold text-gray-900">Yollo Admin</span>
            <div className="ml-auto">
              <SuperAdminNotificheBell token={token} onNavigate={handleNavigate} />
            </div>
          </div>
          {/* Desktop top bar — solo campanella (no menu, c'è sidebar) */}
          <div className="hidden md:flex sticky top-0 z-30 items-center justify-end px-6 py-3 bg-white/80 backdrop-blur-md border-b border-gray-100">
            <SuperAdminNotificheBell token={token} onNavigate={handleNavigate} />
          </div>
          <div className="max-w-5xl mx-auto px-4 py-6">
            <SuperAdminSocietaDetail
              token={token}
              societaId={selectedSocietaId}
              onBack={() => { setSelectedSocietaId(null); fetchData(); }}
              packages={packages}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="superadmin-dashboard">
      <SuperAdminSidebar
        activePage={activePage}
        onNavigate={handleNavigate}
        onLogout={onLogout}
        user={user}
        messaggiBadge={messaggiBadge}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* ═══ MAIN CONTENT ═══ */}
      <main className="flex-1 min-h-screen overflow-y-auto min-w-0">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 md:hidden">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="mobile-menu-btn">
            <MenuIcon className="h-5 w-5 text-gray-600" />
          </button>
          <span className="text-sm font-bold text-gray-900">Yollo Admin</span>
          <div className="ml-auto flex items-center gap-2">
            {messaggiBadge > 0 && (
              <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{messaggiBadge}</span>
            )}
            <SuperAdminNotificheBell token={token} onNavigate={handleNavigate} />
          </div>
        </div>
        {/* Desktop top bar — campanella allineata a destra */}
        <div className="hidden md:flex sticky top-0 z-30 items-center justify-end px-6 py-3 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <SuperAdminNotificheBell token={token} onNavigate={handleNavigate} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">

          {/* ── Page: Abbonamenti ── */}
          {activePage === "company-accounts" && <CompanyAccountsPanel token={token} societies={societies} />}
          {activePage === "societies" && (
            <div className="space-y-6" data-testid="page-societies">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-black tracking-tight text-slate-900" data-testid="page-title-abbonamenti">Gestione Abbonamenti</h2>
                <span className="text-sm text-slate-500">{societies.length} totali</span>
              </div>

              <BackupHealthBanner token={token} onNavigate={handleNavigate} />

              {/* Hub cards */}
              <div className="grid grid-cols-2 gap-3">
                <HubNavCard icon={TrendingUp} title="Analytics" description="Metriche e tendenze" onClick={() => handleNavigate("analytics")} iconBg="#EEF2FF" iconColor="#4f46e5" testId="hub-analytics" />
                <HubNavCard icon={Clock} title="Trial nuovi utenti" description="Configura il periodo di prova" onClick={() => handleNavigate("trial")} iconBg="#FFF7ED" iconColor="#f97316" testId="hub-trial" />
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 rounded-xl bg-white border-slate-200 text-slate-900" placeholder="Cerca per nome..." data-testid="search-societies" />
              </div>

              {filteredSocieties.length === 0 && (
                <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
                  <Building2 className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">{searchQuery ? "Nessun risultato" : "Nessun abbonamento registrato"}</p>
                </div>
              )}

              {/* ── ATTIVI ── */}
              {activeSocs.length > 0 && (
                <div data-testid="section-active">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <h3 className="text-xs font-bold tracking-[0.1em] uppercase text-slate-500">Attivi ({activeSocs.length})</h3>
                  </div>
                  <div className="space-y-3">
                    {[...activeSocs].sort((a,b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(soc => {
                      const sub = soc.subscription || {};
                      const { total, items } = calcSocCost(soc);
                      const remaining = calcRemainingMonths(soc);
                      const isNew = soc.created_at && (new Date() - new Date(soc.created_at)) / (1000 * 60 * 60 * 24) <= 30;
                      const formattedDate = soc.created_at ? new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(soc.created_at)) : 'N/D';

                      return (
                        <div key={soc.id}
                          className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all duration-200 cursor-pointer group"
                          onClick={() => setSelectedSocietaId(soc.id)} data-testid={`society-${soc.id}`}>

                          {/* LEFT: Name + date + meta */}
                          <div className="flex flex-col gap-2 flex-1 min-w-0">
                            <div className="flex items-start justify-between lg:justify-start gap-3">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <h3 className="text-base font-bold tracking-tight text-slate-900 break-words leading-snug" title={soc.denominazione || soc.nome || soc.ragione_sociale || soc.id?.slice(0, 8)}>
                                  {soc.denominazione || soc.nome || soc.ragione_sociale || soc.id?.slice(0, 8)}
                                </h3>
                                {isNew && (
                                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-testid={`new-badge-${soc.id}`}>
                                    Nuovo
                                  </span>
                                )}
                              </div>
                              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0 lg:hidden" />
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500">
                              <span className="flex items-center gap-1.5" data-testid={`date-${soc.id}`}>
                                <Calendar className="h-3 w-3 text-slate-400" />{formattedDate}
                              </span>
                              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{soc.locali_count}</span>
                              <span className="flex items-center gap-1"><Users className="h-3 w-3" />{soc.users_count}</span>
                              {soc.pending_count > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-bold border border-red-100 text-[10px]" data-testid={`pending-badge-${soc.id}`}>
                                  {soc.pending_count} pendenti
                                </span>
                              )}
                            </div>
                            {soc.email && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-400" data-testid={`email-${soc.id}`}>
                                <Mail className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{soc.email}</span>
                              </div>
                            )}
                          </div>

                          {/* CENTER: Cost + remaining + badges */}
                          <div className="flex flex-row items-center justify-between lg:justify-end gap-4 flex-shrink-0 pt-3 lg:pt-0 border-t lg:border-0 border-slate-100">
                            <div className="hidden sm:flex flex-wrap gap-1.5 max-w-[180px]">
                              {items.slice(0, 2).map((item, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium border border-slate-200 bg-slate-50 text-slate-600 truncate max-w-[110px]">{item}</span>
                              ))}
                              {items.length > 2 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium border border-slate-200 bg-slate-50 text-slate-500">+{items.length - 2}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right" data-testid={`cost-${soc.id}`}>
                                {sub.gratuito ? (
                                  <span className="text-sm font-bold text-emerald-600">Gratuito</span>
                                ) : (
                                  <>
                                    <p className="text-lg font-black tracking-tight text-slate-900" data-testid={`cost-value-${soc.id}`}>
                                      {typeof soc.costo_mensile_pieno === "number" && soc.costo_mensile_pieno > total && (
                                        <span className="text-xs font-semibold text-slate-300 line-through mr-1.5" data-testid={`cost-full-${soc.id}`}>{soc.costo_mensile_pieno}€</span>
                                      )}
                                      {total > 0 ? `${total}€` : "N/D"}<span className="text-xs font-normal text-slate-400">/mese</span>
                                    </p>
                                    <p className="text-[10px] text-slate-400 capitalize">Piano {sub.durata || "annuale"}</p>
                                    {soc.sconto_info && (
                                      <span
                                        className="inline-block mt-0.5 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200"
                                        title={`Prezzo ${soc.sconto_info.durata_sconto === "primo_mese" ? "scontato solo sul 1° mese" : "scontato grazie al codice"} ${soc.sconto_info.codice}`}
                                        data-testid={`sconto-badge-${soc.id}`}
                                      >
                                        {soc.sconto_info.codice} {soc.sconto_info.tipo_sconto === "percentuale" ? `-${soc.sconto_info.valore}%` : `-${soc.sconto_info.valore}€`}{soc.sconto_info.durata_sconto === "primo_mese" ? " · 1° mese" : ""}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              {remaining && (
                                <div className="text-right px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-100 min-w-[60px]" data-testid={`remaining-${soc.id}`}>
                                  {remaining.type === "trial" ? (
                                    <>
                                      <p className="text-base font-black text-indigo-600 leading-none">{remaining.days}<span className="text-[10px] font-medium text-indigo-400">gg</span></p>
                                      <p className="text-[9px] text-indigo-400 font-bold uppercase mt-0.5">Trial</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-base font-black text-slate-700 leading-none">{remaining.months}<span className="text-[10px] font-medium text-slate-400">m</span></p>
                                      <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5">Rimasti</p>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* RIGHT: Actions */}
                          <div className="flex items-center gap-2 w-full lg:w-auto" onClick={e => e.stopPropagation()}>
                            <Button size="sm" variant="outline" onClick={async () => {
                              const token = localStorage.getItem("sa_token");
                              try {
                                await axios.post(`${API}/superadmin/resend-credentials/${soc.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success("Credenziali reinviate all'amministratore");
                              } catch(err) {
                                toast.error(err.response?.data?.detail || "Errore nell'invio");
                              }
                            }}
                              className="flex-1 lg:flex-none rounded-lg text-xs font-semibold border-slate-200 text-indigo-600 bg-white hover:bg-indigo-50 hover:border-indigo-200 transition-colors" data-testid={`resend-creds-${soc.id}`}>
                              <Mail className="h-3 w-3 mr-1" />Credenziali
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => toggleSoc(soc)}
                              className="flex-1 lg:flex-none rounded-lg text-xs font-semibold border-slate-200 text-red-600 bg-white hover:bg-red-50 hover:border-red-200 transition-colors" data-testid={`toggle-${soc.id}`}>
                              <X className="h-3 w-3 mr-1" />Disattiva
                            </Button>
                            <ChevronRight className="hidden lg:block h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── NON ATTIVI ── */}
              {inactiveSocs.length > 0 && (
                <div className={activeSocs.length > 0 ? "mt-10" : ""} data-testid="section-inactive">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-slate-400" />
                    <h3 className="text-xs font-bold tracking-[0.1em] uppercase text-slate-400">Non Attivi ({inactiveSocs.length})</h3>
                  </div>
                  <div className="space-y-3 opacity-90">
                    {inactiveSocs.map(soc => {
                      const sub = soc.subscription || {};
                      const { total, items } = calcSocCost(soc);
                      return (
                        <div key={soc.id}
                          className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-slate-400 transition-all duration-200 cursor-pointer group"
                          onClick={() => setSelectedSocietaId(soc.id)} data-testid={`society-${soc.id}`}>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2.5 mb-1">
                            <h3 className="text-base font-bold tracking-tight text-slate-500 break-words">{soc.nome || soc.ragione_sociale || soc.denominazione || soc.id?.slice(0, 8)}</h3>
                              <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-slate-100 text-slate-500 border border-slate-300">OFF</span>
                              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{soc.locali_count}</span>
                              <span className="flex items-center gap-1"><Users className="h-3 w-3" />{soc.users_count}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 flex-shrink-0">
                            <div className="text-right">
                              {sub.gratuito ? (
                                <span className="text-sm font-semibold text-slate-400">Gratuito</span>
                              ) : (
                                <>
                                  <p className="text-lg font-black text-slate-400">{total > 0 ? `${total}€` : "N/D"}<span className="text-xs font-medium text-slate-300">/mese</span></p>
                                  {soc.sconto_info && (
                                    <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200" title={`Codice sconto ${soc.sconto_info.codice}`}>
                                      {soc.sconto_info.codice} {soc.sconto_info.tipo_sconto === "percentuale" ? `-${soc.sconto_info.valore}%` : `-${soc.sconto_info.valore}€`}{soc.sconto_info.durata_sconto === "primo_mese" ? " · 1° mese" : ""}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {items.map((item, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border border-slate-200 bg-slate-50 text-slate-500">{item}</span>
                              ))}
                            </div>
                            <div onClick={e => e.stopPropagation()}>
                              <Button size="sm" onClick={() => toggleSoc(soc)}
                                className="rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 hover:text-white transition-colors" data-testid={`toggle-${soc.id}`}>
                                <Check className="h-3 w-3 mr-1" />Attiva
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Page: Pacchetti ── */}
          {activePage === "packages" && (
            <div className="space-y-4" data-testid="page-packages">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Pacchetti & Servizi ({packages.length})</h2>
                <Button onClick={() => openPkgDialog()} className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold transition-colors" data-testid="add-package-btn">
                  <Plus className="h-4 w-4 mr-1" /> Nuovo
                </Button>
              </div>
              {/* Hub card Prezzi */}
              <HubNavCard icon={Tag} title="Prezzi & Sconti" description="Codici sconto e offerte speciali" onClick={() => handleNavigate("prezzi")} iconBg="#FEF9C3" iconColor="#ca8a04" testId="hub-prezzi" />
              {["base", "addon", "servizio"].map(tipo => {
                const pkgsOfType = packages.filter(p => p.tipo === tipo);
                if (pkgsOfType.length === 0) return null;
                return (
                  <div key={tipo}>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{TIPO_LABELS[tipo]}</h3>
                    <div className="space-y-3">
                      {pkgsOfType.map(pkg => (
                        <div key={pkg.id} className={`bg-white rounded-2xl overflow-hidden border shadow-sm ${pkg.in_evidenza ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200'}`} data-testid={`package-${pkg.id}`}>
                          {/* Immagine pacchetto se presente */}
                          {pkg.immagine_url && (
                            <div className="w-full h-24 relative">
                              <img 
                                src={pkg.immagine_url.startsWith("http") ? pkg.immagine_url : `${API}/superadmin/files/${pkg.immagine_url}`}
                                alt={pkg.nome}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                            </div>
                          )}
                          <div className={`${pkg.immagine_url ? 'p-4 pt-2' : 'p-5'}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-bold text-gray-900">{pkg.nome}</h3>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${TIPO_COLORS[pkg.tipo]}`}>{TIPO_LABELS[pkg.tipo]}</span>
                                {pkg.in_evidenza && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-yellow-100 text-yellow-700 flex items-center gap-0.5"><Star className="h-2.5 w-2.5" />IN EVIDENZA</span>}
                                {pkg.sconto_percentuale > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-600">-{pkg.sconto_percentuale}%</span>}
                                {pkg.wizard_key && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700 flex items-center gap-0.5" data-testid={`pkg-wizard-badge-${pkg.id}`}>W: {pkg.wizard_key}</span>}
                              </div>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openPkgDialog(pkg)} className="h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="icon" variant="ghost" onClick={() => deletePkg(pkg.id)} className="h-8 w-8 text-slate-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </div>
                            {pkg.descrizione && <p className="text-sm text-gray-500 mb-2">{pkg.descrizione}</p>}
                            <div className="flex items-center gap-4 mb-2 text-sm">
                              <span className="font-bold text-indigo-600">
                                {pkg.sconto_percentuale > 0 ? (
                                  <><span className="line-through text-gray-300 mr-1">€{pkg.prezzo_mensile_annuale}</span>€{(pkg.prezzo_mensile_annuale * (1 - pkg.sconto_percentuale / 100)).toFixed(0)}</>
                                ) : `€${pkg.prezzo_mensile_annuale}`}
                                /mese <span className="text-gray-400 font-normal">(netto + IVA)</span>
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {pkg.features?.map(f => {
                                const feat = features.find(ff => ff.key === f);
                                return <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{feat?.label || f}</span>;
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Page: Venditori ── */}
          {activePage === "venditori" && (
            <div className="space-y-5" data-testid="page-venditori">
              <h2 className="text-2xl font-black tracking-tight text-slate-900">Venditori</h2>
              <div className="grid grid-cols-2 gap-3">
                <HubNavCard icon={BarChart3} title="Report Provvigioni" description="Analisi dei compensi" onClick={() => handleNavigate("report-provvigioni")} iconBg="#EEF2FF" iconColor="#4f46e5" testId="hub-report-provvigioni" />
                <HubNavCard icon={FileText} title="Materiali Vendita" description="Documenti e PDF per venditori" onClick={() => handleNavigate("materiali-vendita")} iconBg="#ECFDF5" iconColor="#059669" testId="hub-materiali-vendita" />
              </div>
              <VenditoriPanel />
            </div>
          )}

          {/* ── Page: Report Provvigioni ── */}
          {activePage === "report-provvigioni" && (
            <div className="space-y-4">
              <BackLink label="Venditori" onClick={() => handleNavigate("venditori")} />
              <ReportProvvigioniPanel />
            </div>
          )}

          {/* ── Page: Materiali Vendita ── */}
          {activePage === "materiali-vendita" && (
            <div className="space-y-4">
              <BackLink label="Venditori" onClick={() => handleNavigate("venditori")} />
              <MaterialiVenditaPanel />
            </div>
          )}

          {/* ── Page: Prezzi & Sconti ── */}
          {activePage === "prezzi" && (
            <div className="space-y-4">
              <BackLink label="Pacchetti" onClick={() => handleNavigate("packages")} />
              <PrezziPanel />
            </div>
          )}

          {/* ── Page: Contrattualistica (ex Contratti) ── */}
          {activePage === "contrattualistica" && <ContrattiTemplatePanel token={token} />}

          {/* ── Page: Log Registrazioni ── */}
          {activePage === "registrazioni-log" && <RegistrazioniLogPanel />}

          {/* ── Page: Diagnostica Email ── */}
          {activePage === "email-diagnostic" && (
            <div className="space-y-4">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <EmailDiagnosticPanel />
            </div>
          )}

          {/* ── Page: Recovery Turni ── */}
          {activePage === "recovery-turni" && (
            <div className="space-y-4">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <RecoveryTurniPanel />
            </div>
          )}

          {/* ── Page: Telegram Centralizzato ── */}
          {activePage === "telegram-central" && (
            <div className="space-y-4" data-testid="page-telegram-central">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <TelegramCentralPanel />
            </div>
          )}

          {/* ── Page: Diagnostica Stripe ── */}
          {activePage === "stripe-diagnostic" && (
            <div className="space-y-4" data-testid="page-stripe-diagnostic">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <StripeDiagnosticPanel />
            </div>
          )}

          {/* ── Page: Backup ── */}
          {activePage === "backup" && (
            <div className="space-y-4">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <BackupPanel />
            </div>
          )}

          {/* ── Page: Account Demo ── */}
          {activePage === "demo" && (
            <div className="space-y-4">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <DemoPanel />
            </div>
          )}

          {/* ── Page: Analytics ── */}
          {activePage === "analytics" && (
            <div className="space-y-4">
              <BackLink label="Abbonamenti" onClick={() => handleNavigate("societies")} />
              <AnalyticsPanel />
            </div>
          )}

          {/* ── Page: Trial Nuovi Utenti ── */}
          {activePage === "trial" && (
            <div className="space-y-5" data-testid="page-trial">
              <BackLink label="Abbonamenti" onClick={() => handleNavigate("societies")} />
              <h2 className="text-2xl font-black tracking-tight text-slate-900">Trial Nuovi Utenti</h2>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5 max-w-sm shadow-sm">
                <p className="text-sm text-gray-500">Configura il periodo di prova per i nuovi account registrati.</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Trial attivo</span>
                  <Switch checked={trialSettings.trial_attivo} onCheckedChange={v => saveTrial("trial_attivo", v)} data-testid="trial-attivo-switch" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Durata (giorni)</span>
                  <div className="flex items-center gap-2">
                    <input type="number" value={trialSettings.trial_default_giorni}
                      onChange={e => saveTrial("trial_default_giorni", parseInt(e.target.value) || 0)}
                      className="w-20 h-9 rounded-xl border border-gray-200 text-center text-sm font-bold text-gray-900 bg-gray-50"
                      data-testid="trial-giorni-input" />
                    <span className="text-sm text-gray-400">gg</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Page: Impostazioni Hub ── */}
          {activePage === "impostazioni" && (
            <div className="space-y-5" data-testid="page-impostazioni">
              <h2 className="text-2xl font-black tracking-tight text-slate-900">Impostazioni</h2>
              <div className="space-y-3">
                <HubNavCard icon={Shield} title="Sicurezza" description="Autenticazione a due fattori (2FA)" onClick={() => handleNavigate("security")} iconBg="#EFF6FF" iconColor="#3b82f6" testId="hub-security" />
                <HubNavCard icon={Lock} title="Audit Sicurezza" description="Postura di sicurezza e ultimo audit" onClick={() => handleNavigate("security-audit")} iconBg="#F5F3FF" iconColor="#6C63FF" testId="hub-security-audit" />
                <HubNavCard icon={Database} title="Backup" description="Gestione e ripristino dati" onClick={() => handleNavigate("backup")} iconBg="#F0FDF4" iconColor="#16a34a" testId="hub-backup" />
                <HubNavCard icon={Mail} title="Diagnostica Email" description="Verifica e ri-spedisci email di conferma/benvenuto" onClick={() => handleNavigate("email-diagnostic")} iconBg="#EEF2FF" iconColor="#6366f1" testId="hub-email-diag" />
                <HubNavCard icon={Calendar} title="Recovery Turni" description="Ricostruisci turni cancellati dalle timbrature" onClick={() => handleNavigate("recovery-turni")} iconBg="#FFF7ED" iconColor="#ea580c" testId="hub-recovery-turni" />
                <HubNavCard icon={Bot} title="Bot Telegram" description="Gestione webhook centralizzato (@yollo_ai_bot + demo)" onClick={() => handleNavigate("telegram-central")} iconBg="#EFF6FF" iconColor="#3b82f6" testId="hub-telegram-central" />
                <HubNavCard icon={CreditCard} title="Diagnostica Stripe" description="Stato API key, webhook ricevuti, repair pacchetti mancanti" onClick={() => handleNavigate("stripe-diagnostic")} iconBg="#F5F3FF" iconColor="#7C3AED" testId="hub-stripe-diagnostic" />
                <HubNavCard icon={FlaskConical} title="Account Demo" description="Configurazione account demo" onClick={() => handleNavigate("demo")} iconBg="#FFF7ED" iconColor="#ea580c" testId="hub-demo" />
              </div>
            </div>
          )}

          {/* ── Page: Sicurezza ── (sub-page di Impostazioni) */}
          {activePage === "security" && (
            <div className="space-y-4" data-testid="page-security">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <h2 className="text-lg font-bold text-gray-900">Autenticazione a Due Fattori (2FA)</h2>
              <SecurityPanel />
            </div>
          )}

          {/* ── Page: Audit Sicurezza ── (sub-page di Impostazioni) */}
          {activePage === "security-audit" && (
            <div className="space-y-4" data-testid="page-security-audit">
              <BackLink label="Impostazioni" onClick={() => handleNavigate("impostazioni")} />
              <Sicurezza embedded={true} />
            </div>
          )}

          {/* ── Page: Settings (Fatturazione) ── */}
          {activePage === "settings" && <SettingsFatturazionePanel headers={headers} />}

          {/* ── Page: Messaggi ── */}
          {activePage === "messaggi" && (
            <div className="space-y-4" data-testid="page-messaggi">
              {/* Tab Header */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
                <button onClick={() => setMessaggiTab("segnalazioni")} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${messaggiTab === "segnalazioni" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid="tab-segnalazioni">
                  <Bug className="h-4 w-4" /> Segnalazioni
                  {segnalazioniNuove > 0 && <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{segnalazioniNuove}</span>}
                </button>
                <button onClick={() => setMessaggiTab("richieste")} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${messaggiTab === "richieste" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid="tab-richieste">
                  <Bell className="h-4 w-4" /> Richieste
                  {pendingTotal > 0 && <span className="w-5 h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{pendingTotal}</span>}
                </button>
                <button onClick={async () => {
                  setMessaggiTab("archivio");
                  if (!archivioLoaded) {
                    try {
                      const res = await axios.get(`${API}/segnalazioni?archived=true`, { headers });
                      setArchivedSegnalazioni(res.data);
                      setArchivioLoaded(true);
                    } catch (e) { console.warn("[SuperAdminDashboard] load archive failed:", e); }
                  }
                }} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${messaggiTab === "archivio" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid="tab-archivio">
                  <Archive className="h-4 w-4" /> Archivio
                  {archivedSegnalazioni.length > 0 && <span className="w-5 h-5 bg-gray-400 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{archivedSegnalazioni.length}</span>}
                </button>
              </div>

              {/* TAB: Segnalazioni */}
              {messaggiTab === "segnalazioni" && (
                <div className="space-y-3">
                  {segnalazioni.length === 0 ? (
                    <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
                      <Bug className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-400">Nessuna segnalazione attiva</p>
                    </div>
                  ) : segnalazioni.map(segn => (
                    <div key={segn.id} className={`bg-white rounded-2xl p-4 border shadow-sm transition-all hover:shadow-md ${segn.stato === "nuovo" ? "border-red-200" : "border-green-200"}`} data-testid={`segn-${segn.id}`}>
                      <div className="flex items-start gap-3 cursor-pointer" onClick={() => { setSegnalazioneDetail(segn); setNotaRisoluzione(""); }}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${segn.stato === "nuovo" ? "bg-red-100" : "bg-green-100"}`}>
                          {segn.stato === "nuovo" ? <Bug className="h-5 w-5 text-red-600" /> : <CheckCircle2 className="h-5 w-5 text-green-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-gray-900 text-sm">{segn.utente_nome || segn.email}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${segn.ruolo === "Proprietario" ? "bg-purple-100 text-purple-700" : segn.ruolo === "Cucina" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{segn.ruolo}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {segn.societa_nome && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{segn.societa_nome}</span>}
                            {segn.locale_nome && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">{segn.locale_nome}</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Pagina: <strong>{segn.pagina_nome || segn.pagina_url}</strong></p>
                          {segn.trascrizione && <p className="text-sm text-gray-700 mt-1 line-clamp-2 flex items-start gap-1"><Volume2 className="h-3 w-3 mt-0.5 text-gray-400 flex-shrink-0" /> {segn.trascrizione}</p>}
                          <p className="text-[10px] text-gray-400 mt-1">{new Date(segn.created_at).toLocaleString("it-IT")}</p>
                        </div>
                      </div>
                      {segn.stato === "risolto" && (
                        <div className="flex justify-end mt-2 pt-2 border-t border-gray-100">
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.put(`${API}/segnalazioni/${segn.id}/archive`, {}, { headers });
                              toast.success("Archiviata");
                              setSegnalazioni(prev => prev.filter(s => s.id !== segn.id));
                              setArchivedSegnalazioni(prev => [...prev, { ...segn, is_archived: true }]);
                              setArchivioLoaded(false);
                            } catch { toast.error("Errore"); }
                          }} className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1" data-testid={`archive-list-${segn.id}`}>
                            <Archive className="h-3 w-3" /> Archivia
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* TAB: Archivio — raggruppato per locale, compatto */}
              {messaggiTab === "archivio" && (
                <div className="space-y-2" data-testid="tab-archivio-content">
                  {archivedSegnalazioni.length === 0 ? (
                    <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
                      <Archive className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-400">Nessuna segnalazione archiviata</p>
                    </div>
                  ) : (() => {
                    const grouped = archivedSegnalazioni.reduce((acc, s) => {
                      const key = s.locale_nome || s.societa_nome || "Senza locale";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(s);
                      return acc;
                    }, {});
                    const sortedKeys = Object.keys(grouped).sort();
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-400">{archivedSegnalazioni.length} segnalazioni archiviate</p>
                          <button onClick={() => setExpandedLocali(prev => {
                            const allExpanded = sortedKeys.every(k => prev[k]);
                            const next = {};
                            sortedKeys.forEach(k => next[k] = !allExpanded);
                            return next;
                          })} className="text-[10px] text-gray-400 hover:text-gray-600 font-medium" data-testid="toggle-all-locali">
                            {sortedKeys.every(k => expandedLocali[k]) ? "Chiudi tutti" : "Espandi tutti"}
                          </button>
                        </div>
                        {sortedKeys.map(localeName => {
                          const items = grouped[localeName];
                          const isOpen = expandedLocali[localeName];
                          return (
                            <div key={localeName} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`archivio-locale-${localeName}`}>
                              <button onClick={() => setExpandedLocali(prev => ({ ...prev, [localeName]: !prev[localeName] }))}
                                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors" data-testid={`archivio-toggle-${localeName}`}>
                                <div className="flex items-center gap-2.5">
                                  <MapPin className="h-3.5 w-3.5 text-indigo-400" />
                                  <span className="text-sm font-semibold text-gray-800">{localeName}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-bold">{items.length}</span>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                              </button>
                              {isOpen && (
                                <div className="border-t border-gray-100">
                                  {items.map(segn => (
                                    <div key={segn.id}
                                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0 group"
                                      onClick={() => { setSegnalazioneDetail(segn); setNotaRisoluzione(""); }}
                                      data-testid={`segn-arch-${segn.id}`}>
                                      <span className="text-[10px] text-gray-400 w-[70px] flex-shrink-0 font-mono">{new Date(segn.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</span>
                                      <span className="text-xs font-semibold text-gray-700 truncate min-w-[80px] max-w-[120px]">{segn.utente_nome || segn.email}</span>
                                      {segn.ruolo && <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${segn.ruolo === "Proprietario" ? "bg-purple-50 text-purple-600" : segn.ruolo === "Cucina" ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600"}`}>{segn.ruolo}</span>}
                                      <span className="text-[11px] text-gray-500 truncate flex-1">{segn.pagina_nome || segn.pagina_url}</span>
                                      {segn.nota_risoluzione && <span className="text-[10px] text-gray-400 truncate max-w-[150px] hidden sm:block">— {segn.nota_risoluzione}</span>}
                                      <button onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!await confirm("Eliminare definitivamente?")) return;
                                        try {
                                          await axios.delete(`${API}/segnalazioni/${segn.id}`, { headers });
                                          toast.success("Eliminata");
                                          setArchivedSegnalazioni(prev => prev.filter(s => s.id !== segn.id));
                                        } catch { toast.error("Errore"); }
                                      }} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 p-0.5 transition-opacity flex-shrink-0" data-testid={`delete-arch-${segn.id}`}>
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* TAB: Richieste */}
              {messaggiTab === "richieste" && (
                <div className="space-y-3">
                  {loadingPending ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                  ) : pendingRequests.length === 0 ? (
                    <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
                      <Bell className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-400">Nessuna richiesta in attesa</p>
                    </div>
                  ) : pendingRequests.map(req => {
                    const isTg = req.tipo === "bot_telegram";
                    const isResolving = resolvingPendingId === req.id;
                    return (
                      <div key={req.id} className="bg-white rounded-2xl p-4 border border-amber-200 shadow-sm" data-testid={`pending-req-${req.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Bot className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 text-sm">{req.societa_nome || req.societa_id}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {isTg ? `Bot Telegram — ${req.quantita || 1} numero/i` : req.tipo}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {new Date(req.created_at).toLocaleString("it-IT")}
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex-shrink-0">In attesa</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={() => resolvePendingRequest(req.id, "approve")}
                            disabled={isResolving}
                            className="flex-1 h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold gap-1.5"
                            data-testid={`approve-req-${req.id}`}
                          >
                            {isResolving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            {req.stato === "da_configurare" ? "Configura bot" : "Attiva e Fattura"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resolvePendingRequest(req.id, "reject")}
                            disabled={isResolving}
                            className="flex-1 h-9 rounded-xl border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold gap-1.5"
                            data-testid={`reject-req-${req.id}`}
                          >
                            <X className="h-3.5 w-3.5" /> Rifiuta
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Dialog: Dettaglio Segnalazione ── */}
          {segnalazioneDetail && (
            <Dialog open={!!segnalazioneDetail} onOpenChange={() => setSegnalazioneDetail(null)}>
              <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl">
                <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Bug className="h-5 w-5 text-red-500" /> Segnalazione da {segnalazioneDetail.utente_nome || segnalazioneDetail.email}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 p-2.5 rounded-lg col-span-2"><span className="text-gray-400">Società</span><br/><strong className="text-gray-900">{segnalazioneDetail.societa_nome || "-"}</strong></div>
                    <div className="bg-gray-50 p-2.5 rounded-lg"><span className="text-gray-400">Locale</span><br/><strong className="text-gray-900">{segnalazioneDetail.locale_nome || "-"}</strong></div>
                    <div className="bg-gray-50 p-2.5 rounded-lg"><span className="text-gray-400">Ruolo</span><br/><strong className={`${segnalazioneDetail.ruolo === "Proprietario" ? "text-purple-700" : segnalazioneDetail.ruolo === "Cucina" ? "text-orange-700" : "text-blue-700"}`}>{segnalazioneDetail.ruolo || "-"}</strong></div>
                    <div className="bg-gray-50 p-2.5 rounded-lg"><span className="text-gray-400">Pagina</span><br/><strong className="text-gray-900">{segnalazioneDetail.pagina_nome}</strong></div>
                    <div className="bg-gray-50 p-2.5 rounded-lg"><span className="text-gray-400">Device</span><br/><strong className="text-gray-900">{segnalazioneDetail.device_info || "-"}</strong></div>
                  </div>
                  {segnalazioneDetail.screenshot_path && (<div><p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1"><Image className="h-3 w-3" /> Screenshot</p><img src={fileUrl(segnalazioneDetail.screenshot_path)} alt="Screenshot" className="w-full rounded-xl border border-gray-200 cursor-pointer" onClick={() => window.open(fileUrl(segnalazioneDetail.screenshot_path), '_blank')} /></div>)}
                  {segnalazioneDetail.trascrizione && (<div className="bg-indigo-50 p-3 rounded-xl border border-indigo-100"><p className="text-[10px] text-indigo-400 uppercase tracking-wide mb-1 flex items-center gap-1"><Volume2 className="h-3 w-3" /> Messaggio vocale</p><p className="text-sm text-gray-800 leading-relaxed">{segnalazioneDetail.trascrizione}</p></div>)}
                  <div className="flex items-center justify-between text-[10px] text-gray-400"><span className="flex items-center gap-1"><ExternalLink className="h-3 w-3" /> {segnalazioneDetail.pagina_url}</span><span>{new Date(segnalazioneDetail.created_at).toLocaleString("it-IT")}</span></div>
                  {segnalazioneDetail.stato === "nuovo" ? (
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <Input value={notaRisoluzione} onChange={e => setNotaRisoluzione(e.target.value)} placeholder="Nota di risoluzione (facoltativa)..." className="h-10 rounded-xl bg-gray-50 border-gray-200 text-gray-900" data-testid="nota-risoluzione" />
                      <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer"><input type="checkbox" checked={archiviaAlResolve} onChange={e => setArchiviaAlResolve(e.target.checked)} className="rounded" /> Archivia automaticamente</label>
                      <Button onClick={async () => { setResolvingId(segnalazioneDetail.id); try { await axios.put(`${API}/segnalazioni/${segnalazioneDetail.id}/resolve`, { nota_risoluzione: notaRisoluzione }, { headers }); } catch { /* resolve potrebbe fare timeout per Telegram ma è già salvato */ } try { if (archiviaAlResolve) { await axios.put(`${API}/segnalazioni/${segnalazioneDetail.id}/archive`, {}, { headers }); setArchivioLoaded(false); } toast.success(archiviaAlResolve ? "Risolta e archiviata!" : "Risolta!"); } catch { toast.success("Risolta! (archiviazione in sospeso)"); } setSegnalazioneDetail(null); setNotaRisoluzione(""); fetchData(); setResolvingId(null); }} disabled={resolvingId === segnalazioneDetail.id} className="w-full h-11 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold" data-testid="resolve-btn">
                        {resolvingId === segnalazioneDetail.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Segna come Risolto</>}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="bg-green-50 p-3 rounded-xl border border-green-100"><p className="text-green-700 font-medium text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Risolta</p>{segnalazioneDetail.nota_risoluzione && <p className="text-xs text-gray-600 mt-1">{segnalazioneDetail.nota_risoluzione}</p>}{segnalazioneDetail.resolved_at && <p className="text-[10px] text-gray-400 mt-1">Il {new Date(segnalazioneDetail.resolved_at).toLocaleString("it-IT")}</p>}</div>
                      {!segnalazioneDetail.is_archived && (<Button onClick={async () => { try { await axios.put(`${API}/segnalazioni/${segnalazioneDetail.id}/archive`, {}, { headers }); toast.success("Archiviata"); setSegnalazioneDetail(null); setArchivioLoaded(false); fetchData(); } catch { toast.error("Errore"); } }} variant="outline" className="w-full h-10 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="archive-btn"><Clock className="h-4 w-4 mr-2" /> Archivia</Button>)}
                      <Button onClick={async () => { if (!await confirm("Eliminare definitivamente?")) return; try { await axios.delete(`${API}/segnalazioni/${segnalazioneDetail.id}`, { headers }); toast.success("Eliminata"); setSegnalazioneDetail(null); setSegnalazioni(prev => prev.filter(s => s.id !== segnalazioneDetail.id)); setArchivedSegnalazioni(prev => prev.filter(s => s.id !== segnalazioneDetail.id)); } catch { toast.error("Errore"); } }} variant="outline" className="w-full h-10 rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-segn-btn"><Trash2 className="h-4 w-4 mr-2" /> Elimina definitivamente</Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </main>

      {/* ═══ DIALOG: Package ═══ */}
      <Dialog open={pkgDialog} onOpenChange={setPkgDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle>{editPkg ? "Modifica" : "Nuovo"} Pacchetto/Servizio</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-gray-600">Nome</Label>
                <Input value={pkgForm.nome} onChange={e => setPkgForm(p => ({...p, nome: e.target.value}))} className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" data-testid="pkg-nome-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-gray-600">Descrizione</Label>
                <Input value={pkgForm.descrizione} onChange={e => setPkgForm(p => ({...p, descrizione: e.target.value}))} className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Tipo</Label>
                <select value={pkgForm.tipo} onChange={e => setPkgForm(p => ({...p, tipo: e.target.value}))}
                  className="w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm">
                  <option value="base">Base</option>
                  <option value="addon">Add-on</option>
                  <option value="servizio">Servizio</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Ordine</Label>
                <Input type="number" value={pkgForm.ordine} onChange={e => setPkgForm(p => ({...p, ordine: parseInt(e.target.value) || 0}))} className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Prezzo/mese (netto + IVA)</Label>
                <Input type="number" step="0.01" value={pkgForm.prezzo_mensile_annuale} onChange={e => setPkgForm(p => ({...p, prezzo_mensile_annuale: parseFloat(e.target.value) || 0}))} className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Sconto % (registrazione)</Label>
                <Input type="number" step="1" value={pkgForm.sconto_percentuale} onChange={e => setPkgForm(p => ({...p, sconto_percentuale: parseFloat(e.target.value) || 0}))} className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" placeholder="0" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={pkgForm.in_evidenza} onCheckedChange={v => setPkgForm(p => ({...p, in_evidenza: v}))} />
                  <span className="text-sm text-gray-700 font-medium flex items-center gap-1"><Star className="h-3.5 w-3.5 text-yellow-500" />In evidenza</span>
                </label>
              </div>
            </div>

            {/* Wizard associato */}
            <div>
              <Label className="text-xs font-semibold text-gray-600 mb-1 block">Wizard di configurazione</Label>
              <select
                value={pkgForm.wizard_key || ""}
                onChange={e => setPkgForm(p => ({ ...p, wizard_key: e.target.value || null }))}
                className="w-full h-11 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3"
                data-testid="pkg-wizard-key-select"
              >
                <option value="">Nessuno</option>
                <option value="haccp">Wizard HACCP</option>
                <option value="personale">Wizard Gestione Personale</option>
                <option value="acquisti">Wizard Acquisti</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Alla prima attivazione del pacchetto parte automaticamente il wizard selezionato.</p>
            </div>
            
            {/* Campo Immagine - Upload */}
            <div>
              <Label className="text-xs font-semibold text-gray-600 mb-1 block">Immagine pacchetto</Label>
              <div className="space-y-2">
                {/* Upload button */}
                <label className="flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-gray-50 border border-dashed border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/50 cursor-pointer transition-colors">
                  <Upload className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Carica immagine</span>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      // Validate size (max 5MB)
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Immagine troppo grande. Max 5MB");
                        return;
                      }
                      
                      const formData = new FormData();
                      formData.append("file", file);
                      
                      try {
                        toast.loading("Caricamento...", { id: "upload" });
                        const res = await axios.post(`${API}/superadmin/upload/image`, formData, {
                          headers: { 
                            Authorization: `Bearer ${localStorage.getItem("sa_token")}`,
                            "Content-Type": "multipart/form-data"
                          }
                        });
                        // Save the storage path - will be served via /api/superadmin/files/
                        setPkgForm(p => ({ ...p, immagine_url: res.data.storage_path }));
                        toast.success("Immagine caricata!", { id: "upload" });
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Errore upload", { id: "upload" });
                      }
                    }}
                    data-testid="pkg-immagine-upload"
                  />
                </label>
                
                {/* Preview */}
                {pkgForm.immagine_url && (
                  <div className="relative">
                    <img 
                      src={pkgForm.immagine_url.startsWith("http") ? pkgForm.immagine_url : `${API}/superadmin/files/${pkgForm.immagine_url}`}
                      alt="Anteprima" 
                      className="w-full h-32 object-cover rounded-xl border border-gray-200"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setPkgForm(p => ({ ...p, immagine_url: "" }))}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-600 mb-2 block">Funzionalità incluse</Label>
              <div className="space-y-3">
                {Object.entries(featureGroups).map(([group, feats]) => (
                  <div key={group}>
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">{group}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {feats.map(f => {
                        const isSelected = pkgForm.features.includes(f.key);
                        const alsoIn = f.also_in || [];
                        return (
                          <button key={f.key} type="button" onClick={() => toggleFeature(f.key)}
                            title={alsoIn.length > 0 ? `Anche in: ${alsoIn.map(p => p.package_nome).join(", ")}` : ""}
                            data-testid={`feature-toggle-${f.key}`}
                            className={`text-left text-xs px-3 py-2 rounded-xl border transition-colors ${
                              isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                                : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                            }`}>
                            {isSelected ? <Check className="h-3 w-3 inline mr-1" /> : null}
                            {f.label}
                            {alsoIn.length > 0 && (
                              <span className="block text-[10px] text-gray-400 mt-0.5">
                                Anche in: {alsoIn.map(p => p.package_nome).slice(0, 2).join(", ")}{alsoIn.length > 2 ? ` +${alsoIn.length - 2}` : ""}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={savePkg} className="w-full rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold transition-colors" data-testid="save-pkg-btn">
              <Save className="h-4 w-4 mr-1" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG: Discount Code ═══ */}
      <Dialog open={codeDialog} onOpenChange={setCodeDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle>{editCode ? "Modifica" : "Nuovo"} Codice Sconto</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-gray-600">Codice (verrà convertito in maiuscolo)</Label>
              <Input value={codeForm.codice} onChange={e => setCodeForm(p => ({...p, codice: e.target.value.toUpperCase()}))}
                className="h-12 rounded-xl bg-gray-50 border-gray-200 text-gray-900 font-mono text-lg tracking-wider" placeholder="BENVENUTO2026" data-testid="code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">Tipo sconto</Label>
                <select value={codeForm.tipo_sconto} onChange={e => setCodeForm(p => ({...p, tipo_sconto: e.target.value}))}
                  className="w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm">
                  <option value="percentuale">Percentuale (%)</option>
                  <option value="fisso">Fisso (€)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Valore ({codeForm.tipo_sconto === "percentuale" ? "%" : "€"})</Label>
                <Input type="number" step="0.01" value={codeForm.valore} onChange={e => setCodeForm(p => ({...p, valore: parseFloat(e.target.value) || 0}))}
                  className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Si applica a</Label>
              <select value={codeForm.applica_a} onChange={e => setCodeForm(p => ({...p, applica_a: e.target.value}))}
                className="w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm">
                <option value="tutti">Tutti i pacchetti e servizi</option>
                {packages.map(pkg => <option key={pkg.id} value={pkg.id}>{pkg.nome} ({TIPO_LABELS[pkg.tipo]})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Durata dello sconto</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { key: "primo_mese", label: "Solo primo mese", desc: "Per campagne marketing" },
                  { key: "ricorrente", label: "Ricorrente", desc: "Sconto permanente" },
                ].map(d => (
                  <button key={d.key} type="button" onClick={() => setCodeForm(p => ({...p, durata_sconto: d.key}))}
                    className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${codeForm.durata_sconto === d.key ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                    <p className={`text-sm font-medium ${codeForm.durata_sconto === d.key ? 'text-indigo-700' : 'text-gray-700'}`}>{d.label}</p>
                    <p className="text-[10px] text-gray-400">{d.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">Scadenza</Label>
                <DateInput value={codeForm.scadenza} onChange={(v) => setCodeForm(p => ({...p, scadenza: v}))} />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Max utilizzi (0 = illimitati)</Label>
                <Input type="number" value={codeForm.max_utilizzi} onChange={e => setCodeForm(p => ({...p, max_utilizzi: parseInt(e.target.value) || 0}))}
                  className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
              <span className="text-sm text-gray-700">Attivo</span>
              <Switch checked={codeForm.attivo} onCheckedChange={v => setCodeForm(p => ({...p, attivo: v}))} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Note interne</Label>
              <Input value={codeForm.note} onChange={e => setCodeForm(p => ({...p, note: e.target.value}))} className="h-11 rounded-xl bg-gray-50 border-gray-200 text-gray-900" placeholder="Es. Codice per campagna Facebook Marzo..." />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCode} className="w-full rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold transition-colors" data-testid="save-code-btn">
              <Save className="h-4 w-4 mr-1" /> Salva Codice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function SettingsFatturazionePanel({ headers }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/superadmin/settings-fatturazione`, { headers });
        setData(res.data || {});
      } catch (e) { console.warn("[SuperAdminDashboard] load settings-fatturazione failed:", e); }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/superadmin/settings-fatturazione`, data, { headers });
      setData(res.data);
      toast.success("Impostazioni salvate");
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const F = (k, v) => setData({ ...data, [k]: v });

  if (loading) return <div className="text-center text-gray-400 py-12">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="page-settings">
      <h2 className="text-2xl font-black tracking-tight text-slate-900">Impostazioni</h2>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-bold text-gray-700">Dati fatturazione Yollo</h3>
        <p className="text-xs text-gray-400">Questi dati vengono mostrati ai venditori per l'emissione delle fatture provvigioni.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500">Societa</label>
            <input value={data.societa || ""} onChange={e => F("societa", e.target.value)} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm mt-1" placeholder="Yollo S.r.l." data-testid="sf-societa" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500">Sede Legale</label>
            <input value={data.sede_legale || ""} onChange={e => F("sede_legale", e.target.value)} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm mt-1" placeholder="Via Roma 10" data-testid="sf-sede" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500">Citta</label>
              <input value={data.citta || ""} onChange={e => F("citta", e.target.value)} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm mt-1" placeholder="Roma" data-testid="sf-citta" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500">P.IVA</label>
              <input value={data.partita_iva || ""} onChange={e => F("partita_iva", e.target.value)} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm mt-1" placeholder="IT12345678901" data-testid="sf-piva" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500">Codice Univoco (SDI)</label>
              <input value={data.codice_univoco || ""} onChange={e => F("codice_univoco", e.target.value)} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm mt-1" placeholder="ABC1234" data-testid="sf-sdi" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500">PEC</label>
              <input value={data.pec || ""} onChange={e => F("pec", e.target.value)} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm mt-1" placeholder="yollo@pec.it" data-testid="sf-pec" />
            </div>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="h-9 px-5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          data-testid="sf-save">
          {saving ? "Salvataggio..." : "Salva impostazioni"}
        </button>
      </div>
    </div>
  );
}


export default SuperAdminDashboard;
